import { SignUpButton, SignInButton, SignedIn, SignedOut, SignOutButton } from '@clerk/clerk-react';
import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { api } from '../utils/api';
import logo from '../logo.svg';

export default function Landing() {
  const { getToken, isLoaded } = useAuth();
  const navigate = useNavigate();
  const [authError, setAuthError] = useState(false);
  const [customerNoMenu, setCustomerNoMenu] = useState(false);

  useEffect(() => {
    if (!isLoaded) return;
    setAuthError(false);
    const check = async () => {
      const token = await getToken();
      if (!token) return;
      const res = await api('/api/users/me', { getToken });
      if (!res.ok) {
        if (res.status === 401) setAuthError(true);
        return;
      }
      const data = await res.json();
      const inCustomerContext = sessionStorage.getItem('gng_customerContext') === '1';
      if (inCustomerContext) {
        const lastSlug = localStorage.getItem('gng_lastVisitedMenu');
        const targetSlug = lastSlug || data.businessSlug;
        if (targetSlug) {
          sessionStorage.removeItem('gng_customerContext');
          navigate(`/m/${targetSlug}`, { replace: true });
          return;
        }
        setCustomerNoMenu(true);
        return;
      }
      if (data.userType === 'owner') {
        navigate(data.needsOnboarding ? '/owner/onboarding' : '/owner/dashboard', { replace: true });
      } else if (data.userType === 'customer') {
        const lastSlug = localStorage.getItem('gng_lastVisitedMenu');
        const target = lastSlug ? `/m/${lastSlug}` : (data.businessSlug ? `/m/${data.businessSlug}` : '/');
        navigate(target, { replace: true });
      } else {
        navigate('/auth/callback?type=owner', { replace: true });
      }
    };
    check();
  }, [isLoaded, getToken, navigate]);

  const callbackUrl = `${window.location.origin}/auth/callback?type=owner`;

  return (
    <>
      <SignedOut>
        <LandingPage callbackUrl={callbackUrl} />
      </SignedOut>
      <SignedIn>
        {customerNoMenu ? (
          <div className="flex flex-col items-center justify-center min-h-[70vh] text-center px-6">
            <h1 className="text-2xl font-bold text-slate-900">You&apos;re all set</h1>
            <p className="text-slate-600 mt-2">Use a menu link to order again.</p>
            <Link to="/" onClick={() => sessionStorage.removeItem('gng_customerContext')} className="mt-4 px-6 py-3 rounded-lg bg-amber-500 hover:bg-amber-600 text-slate-900 font-medium">Go home</Link>
          </div>
        ) : authError ? (
          <div className="flex flex-col items-center justify-center min-h-[70vh] text-center px-6 max-w-md mx-auto">
            <p className="text-slate-600">Your session could not be verified. Try signing out and back in.</p>
            <SignOutButton>
              <button className="mt-4 px-6 py-3 rounded-lg bg-slate-200 hover:bg-slate-300 text-slate-900 font-medium">Sign out and try again</button>
            </SignOutButton>
          </div>
        ) : (
          <div className="flex items-center justify-center min-h-[70vh]">
            <p className="text-slate-500">Redirecting…</p>
          </div>
        )}
      </SignedIn>
    </>
  );
}

/* ─────────────── ANIMATED NUMBER COUNTER ─────────────── */
function AnimatedNumber({ target, suffix = '', prefix = '', duration = 2000 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        const start = performance.now();
        const step = (now) => {
          const progress = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          setCount(Math.floor(eased * target));
          if (progress < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      }
    }, { threshold: 0.3 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [target, duration]);

  return <span ref={ref}>{prefix}{count.toLocaleString()}{suffix}</span>;
}

/* ─────────────── SCROLL-REVEAL WRAPPER ─────────────── */
function Reveal({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setVisible(true); observer.disconnect(); }
    }, { threshold: 0.15 });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(32px)',
        transition: `opacity 0.7s ease ${delay}ms, transform 0.7s ease ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

/* ─────────────── FULL LANDING PAGE ─────────────── */
function LandingPage({ callbackUrl }) {
  const [openFaq, setOpenFaq] = useState(null);
  const [legalModal, setLegalModal] = useState(null);
  const [showSplash, setShowSplash] = useState(true);
  const navbarLogoRef = useRef(null);
  const [targetRect, setTargetRect] = useState(null);

  const measureLogo = useCallback(() => {
    if (navbarLogoRef.current) {
      const r = navbarLogoRef.current.getBoundingClientRect();
      setTargetRect({ left: r.left, top: r.top, width: r.width, height: r.height });
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setShowSplash(false), 3650);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!showSplash) return;
    const id = requestAnimationFrame(() => {
      setTimeout(measureLogo, 50);
    });
    const onResize = () => measureLogo();
    window.addEventListener('resize', onResize);
    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener('resize', onResize);
    };
  }, [showSplash, measureLogo]);

  const toggleFaq = useCallback((i) => {
    setOpenFaq((prev) => (prev === i ? null : i));
  }, []);

  const features = [
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
        </svg>
      ),
      title: 'Share Menu → Earn Rewards',
      desc: 'Customers share your menu with friends to earn points and discounts.',
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      ),
      title: 'Only We Turn Shares into Sales',
      desc: 'Every order and share brings more customers to your truck.',
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: 'Order Ahead',
      desc: 'Customers order ahead and skip the line.',
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      ),
      title: 'Increased Sales',
      desc: 'Online ordering increases sales per hour.',
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
        </svg>
      ),
      title: 'QR Powered Pickup',
      desc: 'Customers show a QR code and grab their order instantly.',
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      title: 'QR Secured Pickup',
      desc: 'Customers show a QR code to confirm their order.',
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      title: 'Instant Payments',
      desc: 'Accept payments directly through Stripe without POS hardware.',
    },
  ];

  const steps = [
    { num: '1', title: 'Sign Up & Add Your Menu', desc: 'Create your account, add your items, set your hours. You\'re live in minutes.' },
    { num: '2', title: 'Set Points & Rewards', desc: 'Choose points per share and per referral. Pick rewards: 10% off, free drink, free side, or a custom reward. Customers earn points when they share your menu—when they hit the cap, they claim. You decide what it takes to turn a customer into a marketer.' },
    { num: '3', title: 'Share Your QR Code', desc: 'Print it, tape it to your truck, post it. Customers scan, order, and share your menu to earn rewards. Every share brings new people.' },
    { num: '4', title: 'Your Customers Become Your Marketing Team', desc: 'They share for rewards. Friends discover you. Those friends share. Your menu spreads. You get visible. You make more money.' },
  ];

  const faqs = [
    { q: 'How much does it cost?', a: 'You get 90 days completely free. After that, it\'s just $10/month — less than a single lunch sale. There\'s also a small 10% success fee on orders placed through the platform. That means we only make money when you make money. No POS hardware, no contracts, cancel anytime.' },
    { q: 'Why a success fee instead of a higher monthly price?', a: 'Because we wanted to keep the barrier low. A traditional POS charges $50-200/month whether you sell zero orders or a thousand. Our model is different — we share in your success. On a slow day, you pay almost nothing. On a big day, you keep 90% of the revenue and we earn our cut by helping you get there. It\'s a partnership, not a subscription trap.' },
    { q: 'Do I need special hardware?', a: 'Nope. Just your phone or tablet. Everything runs in a browser — no app downloads needed for you or your customers.' },
    { q: 'How do customers find my menu?', a: 'They scan the QR code on your truck (we generate it for you) or click a link you share on social media, texts, or anywhere.' },
    { q: 'How do I get paid?', a: 'We use Stripe Connect. Payments go directly to your bank account. The 10% platform fee and Stripe processing fees are handled automatically — you just see deposits hit your account. Setup takes 2 minutes during onboarding.' },
    { q: 'Can I customize how my menu looks?', a: 'Absolutely. Choose your colors, fonts, background images, and how item photos are displayed. Make it match your brand.' },
    { q: 'What about refunds?', a: 'You can issue full refunds with one tap from your dashboard. It flows back through Stripe automatically.' },
    { q: 'Can I cancel anytime?', a: 'Yes. No contracts, no cancellation fees. If Grab & Go isn\'t helping you sell more, you can cancel your subscription at any time from your dashboard.' },
  ];

  return (
    <div className="landing-page overflow-x-hidden" style={{ background: '#faf9f7' }}>

      {/* Top bar - navy, logo has white circle for visibility */}
      <div className="fixed top-0 left-0 right-0 z-50 flex items-center justify-between px-5 py-4" style={{ background: 'linear-gradient(to bottom, rgba(15,23,42,0.98) 0%, rgba(15,23,42,0.92) 100%)' }}>
        <Link
          ref={navbarLogoRef}
          to="/"
          className={`flex items-center justify-center shrink-0 w-12 h-12 rounded-full bg-white transition-opacity duration-300 ${showSplash ? 'opacity-0' : 'opacity-100'}`}
          style={{ visibility: showSplash ? 'hidden' : 'visible' }}
        >
          <img src={logo} alt="Grab & Go" className="h-8 w-auto object-contain" />
        </Link>
        <SignInButton mode="redirect" forceRedirectUrl={callbackUrl}>
          <button className="px-5 py-2.5 text-sm font-semibold rounded-lg bg-white text-slate-800 hover:bg-slate-100 transition-colors">
            Sign in
          </button>
        </SignInButton>
      </div>

      {/* Splash overlay - warm cream, logo visible */}
      <div
        className={`fixed inset-0 z-[60] transition-opacity duration-500 ${
          showSplash ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        style={{
          background: 'linear-gradient(180deg, #faf9f7 0%, #f5f2ed 50%, #faf9f7 100%)',
          ...(targetRect && {
            '--splash-x': `${targetRect.left + targetRect.width / 2}px`,
            '--splash-y': `${targetRect.top + targetRect.height / 2}px`,
            '--splash-scale-end': 32 / 128,
          }),
        }}
        aria-hidden={!showSplash}
      >
        {targetRect && (
          <img
            src={logo}
            alt="Grab & Go"
            className="fixed h-32 sm:h-40 w-auto object-contain animate-splash-logo-float origin-center"
          />
        )}
      </div>

      {/* ════════ HERO — Infographic + The Only Menu That Spreads ════════ */}
      <section className="relative flex flex-col items-center px-5 pt-24 pb-16 overflow-hidden" style={{ background: '#faf9f7' }}>
        {/* Subtle warm accent glow */}
        <div className="absolute inset-0 z-0">
          <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[500px] h-[500px] rounded-full opacity-15 blur-[100px]" style={{ background: 'radial-gradient(circle, #fb923c 0%, transparent 70%)' }} />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          {/* Infographic — placed at top like mockup */}
          <Reveal>
            <img src="/landing-page-infographic-4.png" alt="Turn every customer into a crowd. Customers earn rewards and your food truck goes viral." className="w-full h-auto max-w-none mx-auto mb-8 sm:mb-10 rounded-xl" />
          </Reveal>

          {/* Headline */}
          <Reveal delay={80}>
            <h1 className="text-[2.25rem] leading-[1.15] sm:text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight mb-5" style={{ color: '#0f172a' }}>
              The Only Menu That{' '}
              <span className="bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent">Spreads Your Food Truck</span>
            </h1>
          </Reveal>

          {/* Sub-head */}
          <Reveal delay={160}>
            <p className="text-base sm:text-lg leading-relaxed mb-6 max-w-2xl mx-auto text-slate-600">
              Customers share your live menu for rewards. Friends discover your truck, and the loop keeps going.
            </p>
          </Reveal>

          {/* CTA */}
          <Reveal delay={240}>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-4">
              <SignUpButton mode="redirect" forceRedirectUrl={callbackUrl}>
                <button className="w-full sm:w-auto px-8 py-4 text-lg font-bold rounded-xl bg-orange-600 hover:bg-orange-500 text-white transition-all hover:shadow-lg active:scale-[0.98]">
                  Start Free Trial
                </button>
              </SignUpButton>
              <a href="#how-it-works" className="text-slate-600 hover:text-slate-900 text-base font-medium transition-colors flex items-center gap-1">
                See how it works
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
              </a>
            </div>
            <p className="text-sm text-slate-500 mt-4">We bottled word-of-mouth marketing and built it into a menu.</p>
            <p className="text-xs text-slate-400 mt-2 sm:mt-3 pb-20">No credit card required. By signing up you agree to our <button type="button" onClick={() => setLegalModal('terms')} className="underline hover:text-slate-600 transition-colors">Terms of Service</button>.</p>
          </Reveal>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
          <div className="w-6 h-10 rounded-full border-2 border-slate-400 flex items-start justify-center p-1.5">
            <div className="w-1.5 h-3 bg-orange-500 rounded-full animate-bounce" />
          </div>
        </div>
      </section>

      {/* ════════ FOOD TRUCKS GROW: WORD OF MOUTH ════════ */}
      <section className="py-16 sm:py-20 px-5" style={{ background: '#faf9f7' }}>
        <div className="max-w-4xl mx-auto text-center">
          <Reveal>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-800 mb-4">
              Food Trucks Grow One Way: Word of Mouth
            </h2>
            <p className="text-lg max-w-2xl mx-auto text-slate-600 mb-4">
              Great food trucks don&apos;t grow because of ads.
              They grow because someone tells a friend:{' '}
              <b>&quot;You have to try this truck.&quot;</b>
            </p>
            <p className="text-lg max-w-2xl mx-auto text-slate-600">
              Grab & Go accelerates that process by rewarding customers
              for sharing your menu.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ════════ OTHER MENUS VS LIVE MENU ════════ */}
      <section className="py-16 sm:py-20 px-5" style={{ background: 'linear-gradient(180deg, #f5f2ed 0%, #fff8f0 50%, #f5f2ed 100%)' }}>
        <div className="max-w-4xl mx-auto">
          <Reveal>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-800 text-center mb-12 whitespace-nowrap overflow-x-auto">
              Other Menus vs Live Menu
            </h2>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-6">
            <Reveal delay={0}>
              <div className="p-6 rounded-2xl border-2 border-slate-200 bg-red-50/50 h-full">
                <h3 className="text-lg font-bold text-slate-700 text-center mb-4 whitespace-nowrap">Other Menus</h3>
                <p className="text-sm leading-relaxed text-slate-600 text-center">
                  Customer scans.<br /><br />
                  Orders once and leaves.<br /><br />
                  Your menu disappears forever.
                </p>
              </div>
            </Reveal>
            <Reveal delay={80}>
              <div className="p-6 rounded-2xl border-2 border-orange-300 bg-orange-50/80 h-full relative overflow-hidden">
                <div className="absolute top-0 right-0 w-24 h-24 opacity-10">
                  <span className="text-6xl">🔥</span>
                </div>
                <h3 className="text-lg font-bold text-orange-700 text-center mb-4 relative whitespace-nowrap">Live Menu</h3>
                <p className="text-sm leading-relaxed text-slate-700 text-center relative">
                  Customer scans.<br /><br />
                  Shares your menu for rewards.<br /><br />
                  Friends discover your truck.<br /><br />
                  Your menu spreads.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ════════ SOCIAL PROOF TICKER ════════ */}
      <section className="py-10 border-y border-slate-200" style={{ background: '#f5f2ed' }}>
        <div className="max-w-5xl mx-auto px-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <p className="text-3xl sm:text-4xl font-bold text-orange-600">
                <AnimatedNumber target={83} suffix="%" />
              </p>
              <p className="text-sm mt-1 text-slate-500">Trust friends & family recommendations</p>
            </div>
            <div>
              <p className="text-3xl sm:text-4xl font-bold text-orange-600">
                <AnimatedNumber target={5} suffix="x" />
              </p>
              <p className="text-sm mt-1 text-slate-500">Word-of-mouth drives ~5x more sales</p>
            </div>
            <div>
              <p className="text-3xl sm:text-4xl font-bold text-slate-800">
                <AnimatedNumber target={13} suffix="%" />
              </p>
              <p className="text-sm mt-1 text-slate-500">WOM accounts for ~13% of consumer sales</p>
            </div>
            <div>
              <p className="text-3xl sm:text-4xl font-bold text-slate-800">
                <AnimatedNumber target={2} suffix=" weeks" />
              </p>
              <p className="text-sm mt-1 text-slate-500">WOM often converts within ~2 weeks</p>
            </div>
          </div>
        </div>
      </section>

      {/* ════════ THE PROBLEM: INVISIBILITY ════════ */}
      <section className="py-20 px-5" style={{ background: '#faf9f7' }}>
        <div className="max-w-3xl mx-auto text-center">
          <Reveal>
            <p className="text-orange-600 text-sm font-semibold uppercase tracking-widest mb-4">The problem</p>
            <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-slate-800">
              Food Trucks Fail on Visibility, Not Taste
            </h2>
            <p className="text-lg leading-relaxed text-slate-600">
              Nobody knows you&apos;re there. Word of mouth is the strongest ad—we bottled it. Grab & Go puts it in your menu: customers share for rewards, friends discover you, your visibility explodes. More customers. More revenue. The only menu that does this.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ════════ WHY WE FOCUSED ONLY ON MENUS ════════ */}
      <section className="py-20 px-5" style={{ background: '#f5f2ed' }}>
        <div className="max-w-3xl mx-auto text-center">
          <Reveal>
            <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-slate-800">
              Why We Focused Only On Menus
            </h2>
            <p className="text-lg leading-relaxed mb-4 text-slate-600">
              Most platforms try to do everything:
              POS systems, inventory, scheduling, accounting.
            </p>
            <p className="text-lg leading-relaxed mb-4 text-slate-600">
              And they end up average at all of it.
            </p>
            <p className="text-lg leading-relaxed mb-4 text-slate-600">
              We did the opposite.
            </p>
            <p className="text-lg leading-relaxed text-slate-600">
              We focused on <b className="text-slate-800">one thing.</b>
              Building the most powerful online menu
              a food truck has ever had.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ════════ FEATURES — Your Customers Become Your Marketing Team ════════ */}
      <section className="py-20 px-5" style={{ background: '#f5f2ed' }}>
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <div className="text-center mb-16">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-800">
                Your Customers Become Your Marketing Team
              </h2>
            </div>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {features.map((f, i) => (
              <Reveal key={i} delay={i * 80}>
                <div className="group p-6 rounded-2xl border border-slate-200 bg-white transition-all hover:border-orange-300 hover:shadow-lg h-full flex flex-col items-center text-center">
                  <div className="w-12 h-12 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center mb-4 group-hover:bg-orange-200 transition-colors">
                    {f.icon}
                  </div>
                  <h3 className="text-lg font-bold text-slate-800 mb-2">{f.title}</h3>
                  <p className="text-sm leading-relaxed text-slate-600">{f.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ════════ THE SHIFT — Other Menus vs. Viral Menu ════════ */}
      <section className="py-20 px-5" style={{ background: '#faf9f7' }}>
        <div className="max-w-4xl mx-auto">
          <Reveal>
            <div className="text-center mb-12">
              <p className="text-orange-600 text-sm font-semibold uppercase tracking-widest mb-4">The shift</p>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-800">
                Other Menus vs. A Menu That Makes You Money
              </h2>
            </div>
          </Reveal>

          <div className="grid lg:grid-cols-2 gap-6">
            <Reveal delay={0}>
              <div className="p-6 rounded-2xl border border-red-200 bg-red-50/50 h-full">
                <p className="text-red-600 text-sm font-semibold uppercase tracking-wider mb-5 text-center">Other Online Menus</p>
                <ul className="space-y-4">
                  {[
                    'Your menu never spreads—customer scans once, orders, done',
                    'Sharing means copying a link, opening an app, finding your menu... nobody does that',
                    'Zero viral growth. Zero marketing army. Visibility stays flat.',
                    'Sure, they take orders. So does everyone. That\'s table stakes—not a differentiator.',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-slate-600">
                      <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={150}>
              <div className="p-6 rounded-2xl border border-orange-200 bg-orange-50/50 h-full">
                <p className="text-orange-600 text-sm font-semibold uppercase tracking-wider mb-5 text-center">Grab & Go — Rewards Make You Viral</p>
                <ul className="space-y-4">
                  {[
                    'Rewards turn every customer into your marketing team—they share for points, friends discover you',
                    'Your menu goes viral. Exponential visibility. More people find you. More sales.',
                    'We make you money by making you visible. No other menu does this.',
                    'Orders, lines, pickup—we handle that too. But the real differentiator? Your customers market for you.',
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-slate-700">
                      <svg className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ════════ HOW IT WORKS ════════ */}
      <section id="how-it-works" className="py-20 px-5" style={{ background: '#f5f2ed' }}>
        <div className="max-w-3xl mx-auto">
          <Reveal>
            <div className="text-center mb-16">
              <p className="text-orange-600 text-sm font-semibold uppercase tracking-widest mb-4">How it works</p>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-800">
                Live in 60 Seconds
              </h2>
              <p className="text-lg mt-4 text-slate-600">No tech skills needed. If you can post on Instagram, you can run Grab & Go.</p>
            </div>
          </Reveal>

          <div className="space-y-8">
            {steps.map((step, i) => (
              <Reveal key={i} delay={i * 100}>
                <div className="flex gap-5 items-start">
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-orange-600 text-white font-extrabold text-xl flex items-center justify-center">
                    {step.num}
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-800 mb-1">{step.title}</h3>
                    <p className="text-sm leading-relaxed text-slate-600">{step.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ════════ PRICING ════════ */}
      <section className="py-20 px-5" style={{ background: '#f5f2ed' }}>
        <div className="max-w-lg mx-auto text-center">
          <Reveal>
            <p className="text-orange-600 text-sm font-semibold uppercase tracking-widest mb-4">Simple pricing</p>
            <h2 className="text-3xl sm:text-4xl font-bold mb-8 text-slate-800">
              Less Than One Taco Per Day
            </h2>
          </Reveal>

          <Reveal delay={100}>
            <div className="p-8 rounded-2xl border-2 border-orange-200 bg-white shadow-lg relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-500 to-orange-400" />
              <p className="text-sm font-medium text-orange-600 mb-2">Start with</p>
              <p className="text-6xl font-extrabold text-slate-800 mb-1">90 days</p>
              <p className="text-xl font-medium text-slate-600 mb-6">completely free</p>
              <div className="h-px bg-slate-200 mb-6" />
              <p className="text-3xl font-bold text-slate-800 mb-1">Then $10<span className="text-lg font-normal text-slate-500">/month</span></p>
              <p className="text-sm mb-2 text-slate-600">+ 10% success fee only when you make a sale</p>
              <p className="text-xs mb-8 leading-relaxed max-w-xs mx-auto text-slate-500">
                We only get paid when you do. No upfront costs, no hidden fees — just aligned incentives. Compare that to POS systems charging $50-200/mo whether you sell or not.
              </p>
              <ul className="text-left space-y-3 mb-8 text-sm text-slate-700">
                {[
                  'Unlimited menu items & categories',
                  'Unlimited orders',
                  'Real-time order dashboard',
                  'QR code pickup system',
                  'Instant Stripe payments — no POS hardware',
                  'Fully customizable menu theme',
                  'Auto text notifications to customers',
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <svg className="w-5 h-5 text-orange-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                    {item}
                  </li>
                ))}
              </ul>
              <SignUpButton mode="redirect" forceRedirectUrl={callbackUrl}>
                <button className="w-full py-4 text-lg font-bold rounded-xl bg-orange-600 hover:bg-orange-500 text-white transition-all hover:shadow-lg active:scale-[0.98]">
                  Start Free Trial
                </button>
              </SignUpButton>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ════════ FAQ ════════ */}
      <section className="py-20 px-5" style={{ background: '#faf9f7' }}>
        <div className="max-w-2xl mx-auto">
          <Reveal>
            <div className="text-center mb-12">
              <p className="text-orange-600 text-sm font-semibold uppercase tracking-widest mb-4">FAQ</p>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-800">
                Got Questions?
              </h2>
            </div>
          </Reveal>

          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <Reveal key={i} delay={i * 60}>
                <div className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm">
                  <button
                    type="button"
                    onClick={() => toggleFaq(i)}
                    className="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-slate-50 transition-colors"
                  >
                    <span className="font-semibold text-slate-800 pr-4">{faq.q}</span>
                    <svg
                      className={`w-5 h-5 flex-shrink-0 text-orange-500 transition-transform ${openFaq === i ? 'rotate-180' : ''}`}
                      fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {openFaq === i && (
                    <div className="px-6 pb-5">
                      <p className="text-sm leading-relaxed text-slate-600">{faq.a}</p>
                    </div>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ════════ FINAL CTA ════════ */}
      <section className="py-24 px-5 relative overflow-hidden" style={{ background: '#f5f2ed' }}>
        <div className="absolute inset-0 z-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] rounded-full opacity-20 blur-[150px]" style={{ background: 'radial-gradient(circle, #fb923c 0%, transparent 70%)' }} />
        </div>

        <div className="relative z-10 max-w-2xl mx-auto text-center">
          <Reveal>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-6 text-slate-800">
              Let Your Menu Do The Marketing
            </h2>
            <p className="text-lg mb-10 max-w-lg mx-auto text-slate-600">
              Stop posting menus.
              Start spreading them.
            </p>
            <SignUpButton mode="redirect" forceRedirectUrl={callbackUrl}>
              <button className="px-10 py-5 text-xl font-bold rounded-xl bg-orange-600 hover:bg-orange-500 text-white transition-all hover:shadow-lg active:scale-[0.98]">
                Start Free Trial
              </button>
            </SignUpButton>
          </Reveal>
        </div>
      </section>

      {/* ════════ FOOTER ════════ */}
      <footer className="py-10 px-5 border-t border-slate-200" style={{ background: '#0f172a' }}>
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-6">
            <p className="text-sm font-bold text-slate-800">Grab & Go</p>
            <div className="flex items-center gap-6">
              <button type="button" onClick={() => setLegalModal('terms')} className="text-xs text-slate-400 hover:text-white transition-colors">Terms of Service</button>
              <button type="button" onClick={() => setLegalModal('privacy')} className="text-xs text-slate-400 hover:text-white transition-colors">Privacy Policy</button>
            </div>
          </div>
          <p className="text-center text-xs text-slate-500">&copy; {new Date().getFullYear()} Grab & Go. All rights reserved.</p>
        </div>
      </footer>

      {/* Small print under CTAs */}

      {/* ════════ LEGAL MODAL ════════ */}
      {legalModal && (
        <LegalModal type={legalModal} onClose={() => setLegalModal(null)} />
      )}
    </div>
  );
}

/* ─────────────── LEGAL MODAL ─────────────── */
function LegalModal({ type, onClose }) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const isTerms = type === 'terms';

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl max-h-[85vh] bg-white border border-slate-200 shadow-2xl rounded-t-2xl sm:rounded-2xl overflow-hidden flex flex-col z-10">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 flex-shrink-0">
          <h2 className="text-lg font-bold text-slate-800">{isTerms ? 'Terms of Service' : 'Privacy Policy'}</h2>
          <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500 hover:text-slate-800 transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-6 text-sm leading-relaxed text-slate-600">
          {isTerms ? <TermsContent /> : <PrivacyContent />}
        </div>
      </div>
    </div>
  );
}

function TermsContent() {
  return (
    <div className="space-y-6">
      <p className="text-xs uppercase tracking-widest text-slate-400">Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>

      <p>Welcome to Grab & Go (&quot;Platform,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;). By creating an account or using our services, you agree to be bound by these Terms of Service (&quot;Terms&quot;). Please read them carefully.</p>

      <LegalSection title="1. Service Description">
        <p>Grab & Go is a digital menu, ordering, and payment platform designed for food trucks and small food businesses (&quot;Merchants&quot;). The Platform enables Merchants to create digital menus, accept online orders and payments, and manage order fulfillment. Customers (&quot;End Users&quot;) can browse menus, place orders, pay online, and receive pickup notifications.</p>
      </LegalSection>

      <LegalSection title="2. Account Registration">
        <p>To use the Platform as a Merchant, you must create an account, provide accurate information, and maintain the confidentiality of your login credentials. You are responsible for all activity that occurs under your account. You must be at least 18 years old and have the legal authority to enter into these Terms.</p>
      </LegalSection>

      <LegalSection title="3. Subscription & Free Trial">
        <p>Merchants receive a <strong className="text-slate-800">90-day free trial</strong> upon registration. After the trial period, a subscription fee of <strong className="text-slate-800">$10.00 per month</strong> will be charged to the payment method on file. You may cancel your subscription at any time from your dashboard. Cancellation takes effect at the end of the current billing period. No refunds are issued for partial months.</p>
      </LegalSection>

      <LegalSection title="4. Platform Fee & Payment Processing">
        <p>In addition to the monthly subscription, a <strong className="text-slate-800">platform service fee of 10% is applied to the total of each customer order</strong> processed through the Platform. This fee is automatically deducted at the time of payment processing before funds are disbursed to the Merchant.</p>
        <p className="mt-2">Payments are processed through Stripe Connect. Standard Stripe payment processing fees (currently 2.9% + $0.30 per transaction) are charged by Stripe separately and are in addition to the Grab & Go platform fee. We do not control Stripe&apos;s fees and are not responsible for changes to their pricing.</p>
        <p className="mt-2">By using the Platform, you acknowledge and agree to this fee structure. We reserve the right to modify fees with 30 days&apos; written notice to Merchants.</p>
      </LegalSection>

      <LegalSection title="5. Merchant Responsibilities">
        <p>As a Merchant, you agree to: (a) provide accurate menu information including prices, descriptions, and images; (b) fulfill orders in a timely manner; (c) comply with all applicable food safety, health, and business regulations; (d) maintain valid permits and licenses required for your food business; and (e) handle customer complaints and food quality issues directly.</p>
      </LegalSection>

      <LegalSection title="6. Refunds & Disputes">
        <p>Merchants may issue full refunds to customers through the Platform dashboard. Refunds are processed through Stripe Connect. The Grab & Go platform fee on refunded orders may or may not be returned at our discretion. Merchants are responsible for managing customer disputes related to food quality or order accuracy.</p>
      </LegalSection>

      <LegalSection title="7. Acceptable Use">
        <p>You agree not to: (a) use the Platform for any unlawful purpose; (b) upload harmful, fraudulent, or misleading content; (c) interfere with the Platform&apos;s operation or security; (d) attempt to access other users&apos; accounts; or (e) use the Platform to sell prohibited or regulated items without proper licensing.</p>
      </LegalSection>

      <LegalSection title="8. Intellectual Property">
        <p>The Platform, including its design, features, and underlying technology, is owned by Grab & Go. Content you upload (menus, images, logos) remains yours, but you grant us a non-exclusive license to display it within the Platform to operate the service. We may use anonymized, aggregate data for analytics and service improvement.</p>
      </LegalSection>

      <LegalSection title="9. Limitation of Liability">
        <p>To the maximum extent permitted by law, Grab & Go shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to lost profits, lost revenue, or business interruption, arising from your use of the Platform. Our total liability shall not exceed the amount you paid to Grab & Go in the 12 months preceding the claim.</p>
      </LegalSection>

      <LegalSection title="10. Disclaimer of Warranties">
        <p>The Platform is provided &quot;as is&quot; and &quot;as available&quot; without warranties of any kind, express or implied. We do not guarantee uninterrupted, error-free, or secure access to the Platform. We are not responsible for the actions of End Users, the quality of food sold, or any transactions between Merchants and their customers beyond payment processing.</p>
      </LegalSection>

      <LegalSection title="11. Indemnification">
        <p>You agree to indemnify, defend, and hold harmless Grab & Go, its officers, employees, and affiliates from any claims, losses, damages, liabilities, and expenses (including attorney&apos;s fees) arising from: (a) your use of the Platform; (b) your violation of these Terms; (c) your violation of any third-party rights; or (d) any food safety or regulatory issues related to your business.</p>
      </LegalSection>

      <LegalSection title="12. Termination">
        <p>We may suspend or terminate your account at any time if you violate these Terms or for any other reason with reasonable notice. You may delete your account and cancel your subscription at any time. Upon termination, your right to use the Platform ceases immediately. Provisions regarding liability, indemnification, and intellectual property survive termination.</p>
      </LegalSection>

      <LegalSection title="13. Modifications">
        <p>We reserve the right to modify these Terms at any time. Changes will be posted on the Platform and take effect 30 days after posting. Continued use of the Platform after changes constitutes acceptance of the updated Terms. Material changes (including fee modifications) will be communicated via email to the address associated with your account.</p>
      </LegalSection>

      <LegalSection title="14. Governing Law">
        <p>These Terms shall be governed by and construed in accordance with the laws of the United States. Any disputes shall be resolved through binding arbitration in accordance with commercial arbitration rules, except where prohibited by law.</p>
      </LegalSection>

      <LegalSection title="15. Contact">
        <p>If you have questions about these Terms, please contact us through the Platform dashboard or by emailing the support address listed on your account page.</p>
      </LegalSection>
    </div>
  );
}

function PrivacyContent() {
  return (
    <div className="space-y-6">
      <p className="text-xs uppercase tracking-widest text-slate-400">Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>

      <p>This Privacy Policy explains how Grab & Go (&quot;Platform,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) collects, uses, and protects your information when you use our services.</p>

      <LegalSection title="1. Information We Collect">
        <p><strong className="text-slate-800">Account Information:</strong> Name, email address, phone number, and authentication data provided through Clerk (our authentication provider).</p>
        <p className="mt-2"><strong className="text-slate-800">Business Information:</strong> Business name, logo, menu content, operating hours, pickup address, and customization preferences.</p>
        <p className="mt-2"><strong className="text-slate-800">Payment Information:</strong> Payment details are collected and processed by Stripe. We do not directly store credit card numbers or full bank account details.</p>
        <p className="mt-2"><strong className="text-slate-800">Order Data:</strong> Order history, transaction amounts, item selections, and fulfillment status.</p>
        <p className="mt-2"><strong className="text-slate-800">Usage Data:</strong> Browser type, device information, IP address, and interaction patterns within the Platform.</p>
      </LegalSection>

      <LegalSection title="2. How We Use Your Information">
        <p>We use collected information to: (a) provide and maintain the Platform; (b) process payments and disbursements; (c) send order notifications and service communications; (d) improve and optimize the Platform experience; (e) enforce our Terms of Service; and (f) comply with legal obligations.</p>
      </LegalSection>

      <LegalSection title="3. Third-Party Services">
        <p>We integrate with the following third-party services that have their own privacy policies:</p>
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li><strong className="text-slate-800">Clerk</strong> — Authentication and user management</li>
          <li><strong className="text-slate-800">Stripe</strong> — Payment processing and merchant disbursements</li>
          <li><strong className="text-slate-800">Textbelt</strong> — SMS notifications to customers</li>
          <li><strong className="text-slate-800">Google Maps</strong> — Address autocomplete for business locations</li>
        </ul>
      </LegalSection>

      <LegalSection title="4. Data Sharing">
        <p>We do not sell your personal information. We share data only: (a) with third-party service providers listed above to operate the Platform; (b) when required by law, regulation, or legal process; (c) to protect the rights, safety, or property of Grab & Go, our users, or the public; or (d) in connection with a merger, acquisition, or sale of assets (with prior notice).</p>
      </LegalSection>

      <LegalSection title="5. Data Security">
        <p>We implement industry-standard security measures to protect your information, including encrypted connections (HTTPS), secure authentication through Clerk, and PCI-compliant payment processing through Stripe. However, no method of transmission or storage is 100% secure.</p>
      </LegalSection>

      <LegalSection title="6. Data Retention">
        <p>We retain your information for as long as your account is active or as needed to provide services. If you delete your account, we will remove your personal information within 30 days, except where retention is required by law or for legitimate business purposes (such as financial record-keeping).</p>
      </LegalSection>

      <LegalSection title="7. Your Rights">
        <p>Depending on your jurisdiction, you may have the right to: (a) access your personal data; (b) correct inaccurate data; (c) request deletion of your data; (d) object to processing of your data; and (e) export your data. To exercise these rights, contact us through the Platform dashboard.</p>
      </LegalSection>

      <LegalSection title="8. Cookies & Tracking">
        <p>The Platform uses essential cookies and local storage for authentication, session management, and user preferences (such as remembering your last visited menu). We do not use third-party advertising trackers.</p>
      </LegalSection>

      <LegalSection title="9. Children&apos;s Privacy">
        <p>The Platform is not intended for users under 13 years of age. We do not knowingly collect personal information from children under 13. If we become aware that a child under 13 has provided us with personal information, we will delete it.</p>
      </LegalSection>

      <LegalSection title="10. Changes to This Policy">
        <p>We may update this Privacy Policy from time to time. Changes will be posted on the Platform with an updated revision date. Material changes will be communicated via email or in-app notification.</p>
      </LegalSection>

      <LegalSection title="11. Contact">
        <p>If you have questions or concerns about this Privacy Policy, please contact us through the Platform dashboard or by emailing the support address listed on your account page.</p>
      </LegalSection>
    </div>
  );
}

function LegalSection({ title, children }) {
  return (
    <div>
      <h3 className="text-base font-semibold text-slate-800 mb-2">{title}</h3>
      {children}
    </div>
  );
}
